/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { CellType, SourceType } from "@nf/common";
import React from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { isServerSideRender } from "@nf/helpers";
import { IListComponentItemProps } from "../../models";
import { ListComponentItemCover } from "../ListComponentItemCover";
import { ListComponentItemHighlight } from "../ListComponentItemHighlight";
import { ListComponentItemPromo } from "../ListComponentItemPromo";
import { ListComponentItemFrame } from "../ListComponentItemFrame";
import { ListComponentItemCustom } from "../ListComponentItemCustom";
import { ListComponentItemCircle } from "../ListComponentItemCircle";
import { ListComponentGenreItemFrame } from "../ListComponentGenreItemFrame";

import styles from "./ListComponentItem.module.scss";

const ItemPlaceholderSSR = dynamic(
  () => import("../../../ListComponentItemSSR/ItemPlaceholderSSR")
);
/**
 * Component to generate current cell in horizontal list. For example a film from category list
 * @exports ListComponentItem [default]
 */
export const ListComponentItem = React.memo(
  ({
    media,
    isActive,
    isHovered,
    isRowActive,
    isRowHovered,
    isPlaceholder = false,
    width = -1,
    withProgress,
    index,
    readOnly,
    cutomListItem,
    query,
    sourceType,
    cellType,
    style,
    renderSSR,
    onClick,
    onMouseEnter,
    onMouseLeave,
  }: IListComponentItemProps) => {
    const router = useRouter();
    const itemKey = `list-item-${index}_${media ? media.Id : ""}`;

    if (isServerSideRender && !renderSSR) {
      return <ItemPlaceholderSSR media={media} router={router} />;
    }

    const renderView = () => {
      const commonProps = {
        key: itemKey,
        media,
        index,
        isActive,
        isPlaceholder,
        readOnly,
        width,
        query,
      };

      if (sourceType === SourceType.GenreList) {
        return (
          <ListComponentGenreItemFrame {...commonProps} cellType={cellType} />
        );
      }

      switch (cellType) {
        case CellType.Cover:
        case CellType.CoverLarge:
          return (
            <ListComponentItemCover {...commonProps} cellType={cellType} />
          );
        case CellType.Highlights:
          return <ListComponentItemHighlight {...commonProps} />;
        case CellType.Promo:
          return <ListComponentItemPromo {...commonProps} />;
        case CellType.Event:
          return (
            <ListComponentItemFrame
              {...commonProps}
              withProgress={withProgress}
            />
          );
        case CellType.Custom:
          return (
            <ListComponentItemCustom
              {...commonProps}
              isHovered={isHovered}
              isRowActive={isRowActive}
              isRowHovered={isRowHovered}
              withProgress={withProgress}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              {cutomListItem ?? <></>}
            </ListComponentItemCustom>
          );
        case CellType.Circle:
          return <ListComponentItemCircle media={media} />;
        case CellType.Frame:
        case CellType.FrameLarge:
        default:
          return (
            <ListComponentItemFrame
              {...commonProps}
              cellType={cellType}
              withProgress={withProgress}
            />
          );
      }
    };

    return (
      <div className={styles.item} style={style}>
        {renderView()}
      </div>
    );
  }
);

export default ListComponentItem;
